import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { ResponsiveImageFragment } from "../../types.graphql"

type Props = Partial<ResponsiveImageFragment> & {
  objectFit?: "cover" | "contain"
  className?: string
  children?: ReactNode
  fadeOnLoad?: boolean
  alt?: string
  max?: boolean
  aspect?: number | boolean | null
  onLoad?: () => void
}

export function ResponsiveImage(props: Props) {
  const imgRef = useRef<HTMLImageElement>(null!)
  const [loaded, setLoaded] = useState(false)

  const dataProps = Object.fromEntries(Object.entries(props).filter(([key]) => key.startsWith("data-"))) as {
    [key: string]: string
  }

  if (env.client) {
    useLayoutEffect(() => {
      // Update the 'sizes' attribute ASAP
      const el = imgRef.current
      if (!el) return
      el.setAttribute("sizes", "200px")
      const ro = new ResizeObserver((entries) => {
        const size = Math.ceil(entries[0].contentRect.width / 40) * 40
        el.setAttribute("sizes", size + "px")
      })
      ro.observe(el)
      return () => ro.disconnect()
    }, [props.mediaItemUrl])
  }

  useEffect(() => {
    if (props.fadeOnLoad === false) return
    let destroyed = false
    const img = imgRef.current
    if (img) {
      if (!img.complete) {
        setLoaded(false)
        img.addEventListener("load", () => {
          if (destroyed) return
          setTimeout(() => {
            setLoaded(true)
          }, 50)
          if (props.onLoad) props.onLoad()
        })
      } else {
        setTimeout(() => {
          setLoaded(true)
        }, 50)
        if (props.onLoad) props.onLoad()
      }
    }
    return () => {
      destroyed = true
    }
  }, [props.mediaItemUrl])

  const aspect =
    props.aspect === false
      ? "unset"
      : props.aspect
        ? props.aspect
        : props.mediaDetails?.width && props.mediaDetails?.height
          ? props.mediaDetails.width / props.mediaDetails.height
          : ""

  if (!props.mediaItemUrl && !props.srcSet) return null

  return (
    <div
      className={twMerge(
        props.max ? "absolute top-0 left-0 !w-full !h-full" : "aspect-[--aspectRatio]",
        props.className,
      )}
      style={
        aspect
          ? ({
              "--aspectRatio": aspect ?? "",
            } as any)
          : undefined
      }
    >
      <img
        ref={imgRef}
        src={props.mediaItemUrl!}
        srcSet={props.srcSet!}
        alt={props.alt || props.caption || ""}
        draggable={false}
        loading="lazy"
        className={twMerge(
          "!w-full !h-full object-center",
          props.fadeOnLoad !== false && "transition-opacity duration-500",
          loaded || props.fadeOnLoad === false ? "opacity-100" : "opacity-0",
          props.objectFit === "contain" ? "object-contain" : "object-cover",
        )}
        sizes="20vw"
        onLoad={props.onLoad}
        {...dataProps}
      />
      {props.children}
    </div>
  )
}
